/* eslint-disable no-magic-numbers -- for now */
/* eslint-disable @typescript-eslint/class-methods-use-this -- for now */
import type { HeliaNode, FileMetadata } from "@/types/poc/helia";

import { NetworkFileService, type FileExistsResult, type FileDataResult, type GetFileDataOptions } from "../abstract/poc/networkfile.service.interface";
import { parseCid } from "./cidUtils";
import { uint8ArrayToBlob } from "./dataUtils";

export class NetworkFileServiceImpl extends NetworkFileService {

  async checkFileExists(heliaNode: HeliaNode, cid: string): Promise<FileExistsResult> {
    try {
      const parsedCid = parseCid(cid);
      await heliaNode.fs.stat(parsedCid);
      return { exists: true, cid };
    } catch {
      return { exists: false, cid };
    }
  }

  async getFileData(heliaNode: HeliaNode, cid: string, options?: GetFileDataOptions): Promise<FileDataResult> {
    try {
      const parsedCid = parseCid(cid);

      // Get file data from Helia — fs.cat() triggers Bitswap on-demand for missing blocks
      const chunks: Uint8Array[] = [];
      let totalSize = 0;

      for await (const chunk of heliaNode.fs.cat(parsedCid, { signal: options?.signal })) {
        chunks.push(chunk);
        totalSize += chunk.length;
        options?.onChunkReceived?.(totalSize);
      }

      // Combine all chunks
      const data = new Uint8Array(totalSize);
      let offset = 0;
      for (const chunk of chunks) {
        data.set(chunk, offset);
        offset += chunk.length;
      }

      return {
        cid,
        data,
        size: totalSize
      };
    } catch (error) {
      throw new Error(`Failed to get file data for ${cid}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  createFileMetadata(cid: string, size: number, sixId: string, providedMetadata?: FileMetadata): FileMetadata {
    if (providedMetadata !== undefined) {
      return { ...providedMetadata, size }; // Update size with actual size
    }

    return {
      id: cid, // Use CID as ID for P2P-created files
      cid,
      name: `file-${cid.substring(0, 8)}`,
      size,
      path: '',
      type: 'application/octet-stream',
      lastModified: Date.now(),
      addedBy: 'unknown',
      sixId,
      uploadedByUserId: 'unknown',
      uploadedByUserName: 'unknown',
    };
  }

  saveToFilesystem(data: Uint8Array, filename: string, mimeType = 'application/octet-stream'): void {
    // Create a blob from the data
    const blob = uint8ArrayToBlob(data, mimeType);

    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;

    // Trigger download
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    URL.revokeObjectURL(url);
  }
}
